import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { LeadsSpreadsheet, LEAD_EXPORT_FIELDS } from "@/components/spreadsheet/leads-spreadsheet";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { BulkActionsDropdown } from "@/components/bulk-actions-dropdown";
import { queryClient } from "@/lib/queryClient";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { SmartSearchBar } from "@/components/smart-search-bar";
import { RecordSuggestionChips } from "@/components/record-suggestion-chips";
import { useGlobalSearchSuggestions } from "@/hooks/use-global-search-suggestions";
import { useIsMobile } from "@/hooks/use-mobile";
import { SpreadsheetSkeleton } from "@/components/spreadsheet-skeleton";
import type { NlFilterSpec, Region, UserRole } from "@shared/schema";

interface LeadsPageProps {
  effectiveRole?: UserRole;
  effectiveUserId?: string;
}

export default function LeadsPage({ effectiveRole, effectiveUserId }: LeadsPageProps) {
  const { user, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [smartSpec, setSmartSpec] = useState<NlFilterSpec | null>(null);

  // Allow deep-linking to a specific lead via `?openLead=<id>` (used by
  // the sidebar quick-find). Wouter's `useLocation` only reflects the
  // pathname, so we read the query string with `useSearch` to make
  // sure same-page navigations (already on /leads) re-trigger this.
  // The spreadsheet picks the id up and opens the View Details
  // dialog; we then strip the param via `navigate` so refreshing
  // the page doesn't keep re-opening it.
  const autoOpenLeadId =
    new URLSearchParams(search).get("openLead") || undefined;
  const handleAutoOpenComplete = () => {
    navigate("/leads", { replace: true });
  };

  const { data: profile } = useQuery<{ role: string; smartSearchEnabled?: boolean }>({
    queryKey: ["/api/profile"],
    enabled: !!user,
  });
  const { data: aiBalance } = useQuery<{ enabled?: boolean }>({
    queryKey: ["/api/ai/balance"],
    enabled: !!user,
  });
  const aiEnabledForWorkspace = !!aiBalance?.enabled;
  const [smartEnabled, setSmartEnabled] = useState<boolean>(false);
  const [smartHydrated, setSmartHydrated] = useState(false);
  // Hydrate the toggle from the resolved profile preference exactly
  // once. After that, the user's clicks (via SmartSearchBar) own the
  // local state and we only refetch the profile when they explicitly
  // PATCH it.
  useEffect(() => {
    if (smartHydrated) return;
    if (typeof profile?.smartSearchEnabled === "boolean") {
      setSmartEnabled(profile.smartSearchEnabled);
      setSmartHydrated(true);
    }
  }, [profile?.smartSearchEnabled, smartHydrated]);

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
    enabled: !!user,
  });


  // Trigram "Did you mean…?" suggestions for the in-page search input.
  // Mirrors the sidebar quick-find behaviour (Task #293) — when fewer
  // than 3 leads match the typed query, surface the same chip row so a
  // single typo still earns a correction prompt. Reuses the shared
  // /api/search/global hook so the React Query cache is shared across
  // every search input that hits the same query string.
  //
  // `searchDraft` tracks the live typed value so the chip row reacts
  // while the user types. `searchQuery` is kept in sync with the draft
  // for immediate table filtering; it also updates on Enter/blur so
  // smart-search commits still work.
  const [searchDraft, setSearchDraft] = useState("");
  const { data: globalSuggestions } = useGlobalSearchSuggestions(
    searchDraft,
    250,
  );
  const leadHitCount = globalSuggestions?.items?.leads.length ?? 0;
  const suggestionTerms = globalSuggestions?.suggestions ?? [];
  const showSuggestions =
    searchDraft.trim().length > 0 &&
    leadHitCount < 3 &&
    suggestionTerms.length > 0;


  const actualRole = profile?.role || "sales_rep";
  const currentRole = effectiveRole || actualRole;
  const isAdmin = currentRole === "admin";
  
  // Permission checking
  const { can } = useEffectivePermissions();
  const canBulkActions = can("leads.feature.bulkActions");
  const isMobile = useIsMobile();

  if (isLoading) {
    return <SpreadsheetSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Please sign in to view leads.</p>
      </div>
    );
  }

  // The search bar lives in the saved-views strip row to the right of the tabs.
  // Built here so the page keeps ownership of the state; forwarded as `savedViewsRightSlot`.
  const savedViewsRightSlot = (
    <SmartSearchBar
      page="leads"
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onDraftChange={(val) => {
        setSearchDraft(val);
        // Immediately filter the table as the user types. The
        // spreadsheet debounces this internally (250 ms) so it's
        // smooth even on fast typing.
        setSearchQuery(val);
      }}
      spec={smartSpec}
      onSpecChange={setSmartSpec}
      smartEnabled={smartEnabled}
      onSmartEnabledChange={setSmartEnabled}
      aiEnabledForWorkspace={aiEnabledForWorkspace}
      placeholder="Search leads..."
      regions={regions}
    />
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 sm:p-6 pb-0 flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Leads</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">
            Manage your sales leads. Click any cell to edit.
          </p>
        </div>
        {canBulkActions && !isMobile && (
          <BulkActionsDropdown
            entityType="leads"
            dataEndpoint="/api/leads"
            fields={LEAD_EXPORT_FIELDS}
            size="sm"
            onImportComplete={() => queryClient.invalidateQueries({ queryKey: ["/api/leads"] })}
            enrichmentData={{ regions }}
          />
        )}
      </div>
      {showSuggestions && (
        <div className="px-3 sm:px-6 mt-2">
          <RecordSuggestionChips
            suggestions={suggestionTerms}
            onSelect={(term) => {
              setSearchQuery(term);
              setSearchDraft(term);
            }}
            containerTestId="suggestions-leads-search"
            buttonTestIdPrefix="button-leads-suggestion"
          />
        </div>
      )}
      <div className="flex-1 overflow-hidden mt-2 sm:mt-4">
        <LeadsSpreadsheet
          userId={effectiveUserId || user.id}
          isAdmin={isAdmin}
          searchQuery={searchQuery}
          autoOpenLeadId={autoOpenLeadId}
          onAutoOpenComplete={handleAutoOpenComplete}
          smartFilters={smartSpec}
          savedViewsRightSlot={savedViewsRightSlot}
        />
      </div>
    </div>
  );
}
